import {
  Component,
  ElementRef,
  ViewChild,
  ChangeDetectorRef,
} from '@angular/core';

import { environment } from 'src/environments/environment';

import { AttributeDataService } from '../../services/attribute-data.service';
import { ComponentsService } from '../../services/components.service';
import { TemplateEditorUtilsService } from '../../services/template-editor-utils.service';
import { MediaSelectorService } from '../services/media-selector.service';
import { StorageManagerService } from 'src/app/storage/services/storage-manager.service';
import { FileExistenceCheckService } from '../services/file-existence-check.service';

import { DocumentService } from 'src/app/storage/services/document.service';
import { StorageUtilsService } from 'src/app/storage/services/storage-utils.service';
import { BasePlaylistService } from '../services/base-playlist.service';
import { StorageCacheBusterService } from '../../services/storage-cache-buster.service';
import { AnalyticsFactory } from 'src/app/ajs-upgraded-providers';
import { CompanyStateService } from 'src/app/auth/services/company-state.service';
import { LogoImageService } from '../services/logo-image.service';
import { FileMetadataUtilsService } from '../services/file-metadata-utils.service';
import { AiCreditsService } from 'src/app/settings/services/ai-credits.service';
import { ImageAiService } from '../services/image-ai.service';

@Component({
  selector: 'template-component-folder',
  templateUrl: './folder.component.html',
  styleUrls: ['./folder.component.scss'],
  standalone: false,
})
export class FolderComponent {
  componentId: string;
  folderName = '';
  folderPath = '';
  fileType = '';
  thumbnailUrl = '';
  videoPreviewUrl = '';
  folderTopThumbnailUrl = '';
  folderTopIsVideo = false;
  thumbnailLoading = false;
  readonly minPlayCount = 1;
  readonly maxPlayCount = 100000;
  readonly defaultImageThumbnail = LogoImageService.DEFAULT_IMAGE_THUMBNAIL;
  private fileCheckRequestId = 0;
  values = {
    duration: 0,
    transition: null,
    source: '',
    type: '',
    volume: 0,
    subtitlesSource: '',
    centerSubtitles: false,
    shuffle: false,
    resume: false,
    playCountEnabled: false,
    playCount: 20,
    fileMissing: false,
    subtitlesFileMissing: false,
    assetFiles: null,
  };
  processing = false;
  conversionError = '';
  isPlaylistItem: boolean = false;

  get typeName(): string {
    switch (this.values.type) {
      case MediaSelectorService.FOLDER_TYPE.STORAGE_FILE:
      case MediaSelectorService.FOLDER_TYPE.STORAGE_FOLDER:
        return 'Storage';
      case BasePlaylistService.FOLDER_TYPE.GOOGLE_PHOTOS_ALBUM:
        return 'Google Photos';
      case BasePlaylistService.FOLDER_TYPE.CANVA:
        return 'Canva';
      default:
        return 'Folder';
    }
  }

  get isDocument(): boolean {
    return this.fileType === StorageManagerService.FILE_TYPE.DOCUMENT;
  }

  get isImage(): boolean {
    return this.fileType === StorageManagerService.FILE_TYPE.IMAGE;
  }

  get isVideo(): boolean {
    return this.fileType === StorageManagerService.FILE_TYPE.VIDEO;
  }

  get isGooglePhotosAlbum(): boolean {
    return (
      this.values.type === BasePlaylistService.FOLDER_TYPE.GOOGLE_PHOTOS_ALBUM
    );
  }

  get isCanva(): boolean {
    return this.values.type === BasePlaylistService.FOLDER_TYPE.CANVA;
  }

  get shouldShowSummary(): boolean {
    const isStorageFile =
      this.values.type === MediaSelectorService.FOLDER_TYPE.STORAGE_FILE;
    const isStorageFolder =
      this.values.type === MediaSelectorService.FOLDER_TYPE.STORAGE_FOLDER;
    const notCanva = !this.storageManagerService.isCanva(this.values.source);

    return (
      (isStorageFile && (this.isImage || this.isVideo) && notCanva) ||
      isStorageFolder
    );
  }

  get summaryThumbnailKind(): 'image' | 'video' | 'folder' {
    if (this.values.type === MediaSelectorService.FOLDER_TYPE.STORAGE_FOLDER) {
      return 'folder';
    }
    return this.isVideo ? 'video' : 'image';
  }

  get summaryName(): string {
    if (this.summaryThumbnailKind === 'folder') {
      return `${this.typeName} - ${this.folderName}`;
    }
    return this.storageUtilsService.fileNameWithoutExtension(this.folderName);
  }

  get summarySubtitle(): string {
    if (this.summaryThumbnailKind === 'folder') {
      return 'Folder';
    }
    return this.storageUtilsService.getFileType(this.values.source);
  }

  get summaryReplaceLabel(): string {
    switch (this.summaryThumbnailKind) {
      case 'folder':
        return 'Replace Folder';
      case 'video':
        return 'Replace Video';
      default:
        return 'Replace Image';
    }
  }

  constructor(
    private elementRef: ElementRef,
    private changeDetectorRef: ChangeDetectorRef,
    private componentsService: ComponentsService,
    private attributeDataService: AttributeDataService,
    private templateEditorUtilsService: TemplateEditorUtilsService,
    public storageManagerService: StorageManagerService,
    public mediaSelectorService: MediaSelectorService,
    private fileExistenceCheckService: FileExistenceCheckService,
    private documentService: DocumentService,
    private storageUtilsService: StorageUtilsService,
    private storageCacheBusterService: StorageCacheBusterService,
    private analyticsFactory: AnalyticsFactory,
    private companyStateService: CompanyStateService,
    private fileMetadataUtilsService: FileMetadataUtilsService,
    private aiCreditsService: AiCreditsService,
    private imageAiService: ImageAiService,
  ) {
    this.componentsService.registerDirective({
      type: 'rise-folder',
      element: this.elementRef.nativeElement,
      show: (options?) => {
        this.componentId = this.componentsService.selected.id;

        this.isPlaylistItem = options?.playlist ? options.playlist : false;

        this._init();
      },
      getName: (componentId: string): string => {
        const type = this.attributeDataService.getAvailableAttributeData(
          componentId,
          'type',
        );

        if (type === BasePlaylistService.FOLDER_TYPE.GOOGLE_PHOTOS_ALBUM) {
          return (
            this.attributeDataService.getAvailableAttributeData(
              componentId,
              'name',
            ) || ComponentsService.GooglePhotosItem.title
          );
        }

        if (type === BasePlaylistService.FOLDER_TYPE.CANVA) {
          const source = this.attributeDataService.getAvailableAttributeData(
            componentId,
            'source',
          );
          if (source) {
            return this.storageUtilsService.fileNameWithoutExtension(source);
          }
          return null;
        }

        return this.storageUtilsService.parseFolderName(
          this.attributeDataService.getAvailableAttributeData(
            componentId,
            'source',
          ),
        );
      },
      getIcon: (componentId: string): string => {
        const type = this.attributeDataService.getAvailableAttributeData(
          componentId,
          'type',
        );

        if (type === MediaSelectorService.FOLDER_TYPE.STORAGE_FILE) {
          const source = this.attributeDataService.getAvailableAttributeData(
            componentId,
            'source',
          );

          return this.storageManagerService.isCanva(source)
            ? 'component-canva'
            : 'component-' + this.storageManagerService.getFileType(source);
        }
        if (type === BasePlaylistService.FOLDER_TYPE.GOOGLE_PHOTOS_ALBUM) {
          return ComponentsService.GooglePhotosItem.icon;
        }
        if (type === BasePlaylistService.FOLDER_TYPE.CANVA) {
          return 'component-canva';
        }
      },
      getLabel: (componentId: string): string => {
        if (this.fileType === StorageManagerService.FILE_TYPE.DOCUMENT) {
          return 'Document Player';
        }

        if (this.isImage) {
          return 'Image';
        }

        if (this.isVideo) {
          return 'Video';
        }

        const type = this.attributeDataService.getAvailableAttributeData(
          componentId,
          'type',
        );

        if (type === BasePlaylistService.FOLDER_TYPE.GOOGLE_PHOTOS_ALBUM) {
          return ComponentsService.GooglePhotosItem.title;
        }

        if (type === BasePlaylistService.FOLDER_TYPE.CANVA) {
          return 'Canva';
        }
      },
      onPresentationOpen: () => {
        this.storageCacheBusterService.loadStorageFiles();
      },
      canShowAiButton: (componentId?: string) =>
        this.canGenerateWithAI(componentId),
      onAiHandler: () => {
        if (!this.canGenerateWithAI()) {
          return false;
        }

        this.generateWithAI();
        return true;
      },
    });
  }

  canGenerateWithAI(componentId?: string): boolean {
    return (
      this._isImageComponent(componentId) && this.aiCreditsService.canUseAi()
    );
  }

  private _isImageComponent(componentId?: string): boolean {
    if (!componentId) {
      return this.isImage;
    }

    const source = this.attributeDataService.getAvailableAttributeData(
      componentId,
      'source',
    );

    return (
      this.storageManagerService.getFileType(source) ===
      StorageManagerService.FILE_TYPE.IMAGE
    );
  }

  generateWithAI(): void {
    this.mediaSelectorService.onUserComponentAdd = null;
    this.mediaSelectorService.onMediaUpdate = this.updateSource.bind(this);
    this.imageAiService.showGenerateImage((file) =>
      this.mediaSelectorService.storageFilesSelected(
        [file],
        StorageManagerService.FILE_TYPE.IMAGE,
      ),
    );
  }

  private _init() {
    const original = this.values.source;
    this.values.duration =
      this.attributeDataService.getAvailableAttributeData(
        this.componentId,
        'duration',
      ) || 10;
    this.values.transition =
      this.attributeDataService.getAvailableAttributeData(
        this.componentId,
        'transition-type',
      ) || '';
    this.values.source =
      this.attributeDataService.getAvailableAttributeData(
        this.componentId,
        'source',
      ) || '';
    this.values.subtitlesSource =
      this.attributeDataService.getAvailableAttributeData(
        this.componentId,
        'subtitlesSource',
      ) || '';
    this.values.centerSubtitles =
      this.attributeDataService.getAvailableAttributeData(
        this.componentId,
        'centerSubtitles',
      ) || false;
    this.values.type = this.attributeDataService.getAvailableAttributeData(
      this.componentId,
      'type',
    );
    const volume =
      this.attributeDataService.getAvailableAttributeData(
        this.componentId,
        'volume',
      ) || 0;
    this.values.volume = this.templateEditorUtilsService.intValueFor(volume, 0);
    this.values.shuffle =
      this.attributeDataService.getAvailableAttributeData(
        this.componentId,
        'shuffle',
      ) || false;
    this.values.resume =
      this.attributeDataService.getAvailableAttributeData(
        this.componentId,
        'resume',
      ) || false;
    this.values.playCountEnabled =
      this.attributeDataService.getAvailableAttributeData(
        this.componentId,
        'playCountEnabled',
      ) || false;
    const playCount = this.attributeDataService.getAvailableAttributeData(
      this.componentId,
      'playCount',
    );
    this.values.playCount = this.templateEditorUtilsService.intValueFor(
      playCount,
      20,
    );

    this.folderName = this.storageUtilsService.parseFolderName(
      this.values.source,
    );
    this.folderPath = this.storageUtilsService.parseStoragePath(
      this.values.source,
    );
    this.fileType = this.storageManagerService.getFileType(this.values.source);
    this.values.fileMissing = false;
    this.values.subtitlesFileMissing = false;
    this.resetThumbnailState();
    this.checkFileExists(this.values.source);
    this.checkSubtitlesFileExists(this.values.subtitlesSource);

    if (this.values.source !== original) {
      this.values.assetFiles = null;
    }
    this.convertFile();
  }

  private convertFile() {
    this.conversionError = '';

    if (this.isDocument && !this.values.assetFiles) {
      let path = this.storageUtilsService.parseFilePath(this.values.source);

      if (!path) {
        return;
      }

      this.processing = true;
      this.documentService
        .convertFile(path[2], path[1])
        .then((count) => {
          this.values.assetFiles = count;
        })
        .catch((error) => {
          console.error(error);
          this.conversionError = error.message;
        })
        .finally(() => {
          this.processing = false;
        });
    }
  }

  private resetThumbnailState(): void {
    this.thumbnailUrl = '';
    this.videoPreviewUrl = '';
    this.folderTopThumbnailUrl = '';
    this.folderTopIsVideo = false;
    this.thumbnailLoading = true;
  }

  onThumbnailLoaded(): void {
    this.thumbnailLoading = false;
  }

  private shouldLoadThumbnail(path: string): boolean {
    return (
      this.values.type === MediaSelectorService.FOLDER_TYPE.STORAGE_FILE &&
      (this.fileType === StorageManagerService.FILE_TYPE.IMAGE ||
        this.fileType === StorageManagerService.FILE_TYPE.VIDEO) &&
      !this.storageManagerService.isCanva(path)
    );
  }

  private _pickFolderTopThumbnail(files: any[]): {
    url: string;
    isVideo: boolean;
  } {
    if (!files || files.length === 0) {
      return { url: '', isVideo: false };
    }
    const SCAN_LIMIT = 5;
    const limit = Math.min(files.length, SCAN_LIMIT);
    for (let i = 0; i < limit; i++) {
      const item = files[i];
      const name = (item && item.name) || '';
      const type = this.storageManagerService.getFileType(name);
      if (
        type !== StorageManagerService.FILE_TYPE.IMAGE &&
        type !== StorageManagerService.FILE_TYPE.VIDEO
      ) {
        continue;
      }
      const thumbnail = this.fileMetadataUtilsService.thumbnailFor(item);
      if (thumbnail) {
        return { url: thumbnail, isVideo: false };
      }
      if (type === StorageManagerService.FILE_TYPE.VIDEO) {
        const poster = this.fileMetadataUtilsService.videoPosterUrlFor(
          item && item.mediaLink,
        );
        if (poster) {
          return { url: poster, isVideo: true };
        }
      }
    }
    return { url: '', isVideo: false };
  }

  private async checkFileExists(path: string): Promise<void> {
    const requestId = ++this.fileCheckRequestId;

    if (!path) {
      this.resetThumbnailState();
      return;
    }

    try {
      if (this.shouldLoadThumbnail(path)) {
        this.resetThumbnailState();
        const metadata =
          await this.fileExistenceCheckService.requestMetadataFor(
            [path],
            LogoImageService.DEFAULT_IMAGE_THUMBNAIL,
          );

        if (requestId !== this.fileCheckRequestId) {
          return;
        }

        const fileMetadata = metadata && metadata[0];
        this.values.fileMissing = !fileMetadata?.exists;
        const returnedUrl =
          fileMetadata?.exists && fileMetadata['thumbnail-url']
            ? fileMetadata['thumbnail-url']
            : '';

        if (this.isVideo) {
          const hasRealThumbnail =
            returnedUrl &&
            returnedUrl !== LogoImageService.DEFAULT_IMAGE_THUMBNAIL;
          if (hasRealThumbnail) {
            this.thumbnailUrl = returnedUrl;
          } else {
            this.videoPreviewUrl =
              this.fileMetadataUtilsService.videoPosterUrlFor(
                fileMetadata?.['media-link'],
              );
          }
          if (!this.thumbnailUrl && !this.videoPreviewUrl) {
            this.thumbnailUrl = LogoImageService.DEFAULT_IMAGE_THUMBNAIL;
            this.onThumbnailLoaded();
          }
        } else {
          this.thumbnailUrl =
            returnedUrl || LogoImageService.DEFAULT_IMAGE_THUMBNAIL;
        }
      } else if (
        this.values.type === MediaSelectorService.FOLDER_TYPE.STORAGE_FILE
      ) {
        this.resetThumbnailState();
        const exists =
          await this.fileExistenceCheckService.checkFileExists(path);

        if (requestId !== this.fileCheckRequestId) {
          return;
        }

        this.values.fileMissing = !exists;
      } else if (
        this.values.type === MediaSelectorService.FOLDER_TYPE.STORAGE_FOLDER
      ) {
        this.resetThumbnailState();
        const files = await this.storageManagerService.loadFiles(
          path.substring(path.indexOf('/') + 1),
          StorageManagerService.FILE_TYPE.IMAGE_VIDEO_FOLDER,
        );

        if (requestId !== this.fileCheckRequestId) {
          return;
        }

        this.values.fileMissing = files.length === 0;
        const topThumbnail = this._pickFolderTopThumbnail(files);
        if (this.values.fileMissing) {
          this.folderTopThumbnailUrl = LogoImageService.DEFAULT_IMAGE_THUMBNAIL;
          this.folderTopIsVideo = false;
        } else {
          this.folderTopThumbnailUrl = topThumbnail.url;
          this.folderTopIsVideo = topThumbnail.isVideo;
        }
        if (!this.folderTopThumbnailUrl) {
          this.onThumbnailLoaded();
        }
      }
    } catch (error) {
      if (requestId !== this.fileCheckRequestId) {
        return;
      }

      this.thumbnailUrl = LogoImageService.DEFAULT_IMAGE_THUMBNAIL;
      this.thumbnailLoading = false;
      this.values.fileMissing = true;
    }
  }

  private async checkSubtitlesFileExists(path: string): Promise<void> {
    if (
      !path ||
      this.values.type !== MediaSelectorService.FOLDER_TYPE.STORAGE_FILE
    ) {
      return;
    }

    try {
      const exists = await this.fileExistenceCheckService.checkFileExists(path);
      this.values.subtitlesFileMissing = !exists;
    } catch (error) {
      this.values.subtitlesFileMissing = true;
    }
  }

  onChange() {
    this.changeDetectorRef.detectChanges();
  }

  saveDuration() {
    this.attributeDataService.setAttributeData(
      this.componentId,
      'duration',
      this.values.duration,
    );
  }

  saveTransition() {
    this.attributeDataService.setAttributeData(
      this.componentId,
      'transition-type',
      this.values.transition,
    );
  }

  saveVolume() {
    this.attributeDataService.setAttributeData(
      this.componentId,
      'volume',
      this.values.volume,
    );
  }

  saveSubtitlesSource() {
    this.attributeDataService.setAttributeData(
      this.componentId,
      'subtitlesSource',
      this.values.subtitlesSource,
    );
    this.attributeDataService.setAttributeData(
      this.componentId,
      'centerSubtitles',
      this.values.centerSubtitles,
    );
  }
  saveShuffle() {
    this.attributeDataService.setAttributeData(
      this.componentId,
      'shuffle',
      this.values.shuffle,
    );
  }

  updateSource(value: string, type: string) {
    this.values.source = value;
    this.values.type = type;

    if (
      type !== BasePlaylistService.FOLDER_TYPE.GOOGLE_PHOTOS_ALBUM &&
      type !== BasePlaylistService.FOLDER_TYPE.CANVA
    ) {
      this.folderName = this.storageUtilsService.parseFolderName(value);
      this.folderPath = this.storageUtilsService.parseStoragePath(value);
    }

    this.fileType = this.storageManagerService.getFileType(this.values.source);
    this.values.fileMissing = false;
    this.resetThumbnailState();
    this.checkFileExists(this.values.source);

    this.values.assetFiles = null;
    this.convertFile();

    this.attributeDataService.setAttributeData(
      this.componentId,
      'source',
      value,
    );
    this.attributeDataService.setAttributeData(this.componentId, 'type', type);
    this.attributeDataService.setAttributeData(
      this.componentId,
      'environment',
      environment.production ? 'prod' : 'test',
    );

    this.componentsService.updateComponentValue(value);
  }

  selectMedia() {
    let selectorFileType;

    if (
      this.values.type === MediaSelectorService.FOLDER_TYPE.STORAGE_FOLDER &&
      !this.isPlaylistItem
    ) {
      // As a user component the file type must be "folder"
      selectorFileType = StorageManagerService.FILE_TYPE.FOLDER;
    } else {
      selectorFileType =
        this.isDocument || this.isVideo || this.isImage
          ? this.fileType
          : undefined;
    }

    this.mediaSelectorService.onUserComponentAdd = null;
    this.mediaSelectorService.onMediaUpdate = this.updateSource.bind(this);
    this.componentsService.editComponent(
      {
        type: 'rise-media-selector',
      },
      {
        fileType: selectorFileType,
      },
    );
  }

  updateSubtitles(value: string) {
    this.values.subtitlesSource = value;
    this.saveSubtitlesSource();

    this.values.subtitlesFileMissing = false;
    this.checkSubtitlesFileExists(this.values.subtitlesSource);

    this.analyticsFactory.track('Folder Component Subtitles Added', {
      companyId: this.companyStateService.getSelectedCompanyId(),
    });
  }

  addSubtitles() {
    this.mediaSelectorService.onUserComponentAdd = null;
    this.mediaSelectorService.onMediaUpdate = this.updateSubtitles.bind(this);
    this.componentsService.editComponent(
      {
        type: 'rise-media-selector',
      },
      {
        fileType: StorageManagerService.FILE_TYPE.SUBTITLES,
        singleFile: true,
      },
    );
  }

  deleteSubtitles() {
    this.values.subtitlesSource = '';
    this.values.subtitlesFileMissing = false;
    this.saveSubtitlesSource();
  }

  centerSubtitlesChanged(checked: boolean) {
    this.values.centerSubtitles = checked;
    this.saveSubtitlesSource();
  }
  toggleShuffle() {
    this.values.shuffle = !this.values.shuffle;
    this.saveShuffle();
  }

  saveResume() {
    this.attributeDataService.setAttributeData(
      this.componentId,
      'resume',
      this.values.resume,
    );
  }

  toggleResume() {
    this.values.resume = !this.values.resume;
    this.saveResume();
  }

  savePlayCount() {
    this.values.playCount = this.clampPlayCount(this.values.playCount);
    this.attributeDataService.setAttributeData(
      this.componentId,
      'playCountEnabled',
      this.values.playCountEnabled,
    );
    this.attributeDataService.setAttributeData(
      this.componentId,
      'playCount',
      this.values.playCount,
    );
  }

  private clampPlayCount(value: number): number {
    return Math.min(
      this.maxPlayCount,
      Math.max(this.minPlayCount, Number(value) || this.minPlayCount),
    );
  }

  togglePlayCountEnabled() {
    this.values.playCountEnabled = !this.values.playCountEnabled;
    this.savePlayCount();
  }
}
